<template>
  <div>
    <div class="form-content">
      <Selector
        ref="Selector"
        :petitionSelector="petitionSelector"
        :donationSelector="donationSelector"
        :petitionHandle="petitionHandle"
      ></Selector>

      <!-- Form -->
      <div>
        <!-- First Name -->
        <div class="wwf-field">
          <input
            name="firstName"
            type="text"
            v-model="supporter.firstName"
            v-validate="'required'"
            :class="{
              error: errors.has('firstName'),
              empty: !supporter.firstName,
            }"
            placeholder=" "
            autocomplete="given-name"
          />
          <label for="firstName">First Name</label>
        </div>

        <!-- Last Name -->
        <div class="wwf-field">
          <input
            name="lastName"
            type="text"
            v-model="supporter.lastName"
            v-validate="'required'"
            :class="{
              error: errors.has('lastName'),
              empty: !supporter.lastName,
            }"
            placeholder=" "
            autocomplete="family-name"
          />
          <label for="lastName">Last Name</label>
        </div>

        <!-- Email -->
        <div class="wwf-field">
          <input
            id="email"
            name="email"
            type="email"
            v-model="supporter.email"
            v-validate="{ required: true, leafReliefCheckEmail: true }"
            :class="{
              error: errors.has('email'),
              empty: !supporter.email,
            }"
            placeholder=" "
            autocomplete="email"
          />
          <label for="email">Email</label>
          <span class="wwf-alert">
            <p v-if="errors.has('email')">
              {{ errors.first("email") }}
            </p>
          </span>
        </div>

        <!-- Postcode -->
        <div class="wwf-field">
          <input
            name="postcode"
            type="text"
            v-model="supporter.postcode"
            v-validate="'required'"
            :class="{
              error: errors.has('postcode'),
              empty: !supporter.postcode,
            }"
            placeholder=" "
            autocomplete="postal-code"
          />
          <label for="postcode">Postcode</label>
        </div>

        <!-- Phone Number -->
        <div class="wwf-field">
          <input
            name="phone"
            type="tel"
            v-model="supporter.phoneNumber"
            min="8"
            max="12"
            v-validate="'required'"
            :class="{
              error: errors.has('phone'),
              empty: !supporter.phoneNumber,
            }"
            placeholder=" "
            autocomplete="tel"
          />
          <label for="phone">Phone</label>
        </div>

        <!-- Display Name -->
        <label class="checkbox">
          Yes, display my name
          <input
            type="checkbox"
            checked
            v-model="supporter.agreesDisplayName"
          />
          <span class="checkmark"></span>
        </label>
      </div>
      <!-- End of form -->

      <div class="submit-email-error" v-if="isEmailInError">
        <p>{{ duplicateEmailMessage }}</p>
      </div>

      <!-- Next button -->
      <button type="button" class="plain" v-on:click="next()" href="#form-top">
        <span v-if="!isSaving">
          Plant a {{ isDonation ? "forest" : "tree" }}
        </span>
        <span v-if="isSaving">
          <i class="far fa-spinner-third fa-spin"></i>
        </span>
      </button>

      <!-- PRIVACY STATEMENT -->
      <div class="privacy-statement">
        <p>
          By signing you accept
          <a href="/privacy-policy/privacy-policy" target="_blank">
            WWF-Australia&rsquo;s Privacy Policy.
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Selector from "./Selector";
import gtm from "../../utils/gtm";

import { mapState, mapGetters } from "vuex";
import { Validator } from "vee-validate";

const { detect } = require("detect-browser");
const browser = detect();

export default {
  name: "LeafReliefDetails",
  components: {
    Selector,
  },

  data() {
    return {
      isSaving: false,
      timeout: null,
      lastTimestamp: Date.now(),
      emailRegex: /.+\@.+\..+/,
      checkEmailLoading: false,
      isEmailInError: false,
    };
  },
  computed: {
    ...mapGetters(["isDonation", "selection"]),
    ...mapState({
      supporter: (state) => state.leafRelief.details,
      donation: (state) => state.donation.donation,
    }),
  },
  props: {
    programName: {
      type: String,
      required: true,
    },
    datalayerName: {
      type: String,
      required: true,
    },

    // Selectors
    petitionSelector: {
      type: String,
      default: "Plant a tree",
    },
    donationSelector: {
      type: String,
      default: "Plant a forest",
    },

    petitionHandle: {
      type: String,
      default: "Show your support and we'll plant a tree today",
    },

    duplicateEmailMessage: {
      type: String,
      default:
        "You can donate multiple times with the same email but you can only submit once the petition.",
    },
  },

  created() {
    // If it's a petition, prevent 2 signatures
    // If it's a donation, allow multiple submissions
    const leafReliefCheckEmail = (email) => {
      this.isEmailInError = false;

      if (this.isDonation) {
        return this.checkEmailSensis(email);
      } else {
        // Do not allow multiple submission for the petition
        return new Promise((resolve, reject) => {
          Promise.all([
            this.checkEmailDuplicate(email),
            this.checkEmailSensis(email),
          ])
            .then((results) => {
              for (const r of results) {
                if (!r.valid) {
                  this.isEmailInError = true;
                  return resolve(r);
                }
              }
              this.errors.remove("email");
              resolve({ valid: true });
            })
            .catch((err) => {
              console.error(err);
              this.errors.remove("email");
              resolve({ valid: true });
            });
        });
      }
    };

    Validator.extend("leafReliefCheckEmail", {
      validate: leafReliefCheckEmail,
      getMessage: (field, params, data) => data.message,
    });
  },
  methods: {
    checkEmailDuplicate(email) {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("checkEmail", {
            campaignId: this.datalayerName,
            email: email,
          })
          .then((result) => {
            if (result) {
              resolve({
                valid: false,
                field: "email",
                data: { message: "Your email has already been submitted" },
              });
            } else {
              resolve({ valid: true });
            }
          });
      });
    },
    checkEmailSensis(email) {
      return new Promise((resolve, reject) => {
        if (email.length > 0 && this.emailRegex.test(email)) {
          let timestamp = Date.now(); // timestamp for this api call
          this.lastTimestamp = timestamp; // 1 , 2 , 3
          this.checkEmailLoading = true;
          // Fetch Suggestions
          this.$store
            .dispatch("validateEmail", email)
            .then((response) => {
              if (response && timestamp >= this.lastTimestamp) {
                this.checkEmailLoading = false;
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              this.checkEmailLoading = false;
              // If it fails, let the user free to continue
              resolve({ valid: true });
            });
        } else {
          resolve({
            valid: false,
            data: { message: "Email invalid" },
          });
        }
      });
    },
    async next() {
      try {
        // Exit the function if it's busy
        if (this.isSaving) {
          return;
        }
        this.isSaving = true;

        if (this.isDonation) {
          // Validate the selector as well
          await Promise.all([this.validate(), this.$refs.Selector.validate()]);
          // If it's a donation, save the pending donation and go to the next step for payment
          await this.saveDonation();
        } else {
          // Validate the form
          await this.validate();
          // If it's a petition, save the lead and go to the next step
          await this.savePetition();
          this.$store.commit("resetLeafRelief");
        }

        this.$emit("next");
      } catch (err) {
        console.error(err);
        gtm.formSubmitEvent(this.datalayerName);
      } finally {
        this.isSaving = false;
      }
    },

    validate() {
      return new Promise((resolve, reject) => {
        this.$validator
          .validateAll()
          .then((result) => {
            // Will return true if valid and false if not
            if (result) {
              resolve(result);
            } else {
              console.error("Leaf Relief - Details not valid - result", result);
              reject(result);
            }
          })
          .catch((err) => {
            console.error("Leaf Relief - Details - Error - ", err);
            reject(err);
          });
      });
    },

    async savePetition() {
      try {
        const payload = {
          email: this.supporter.email,
          firstName: this.supporter.firstName,
          lastName: this.supporter.lastName,
          postcode: this.supporter.postcode,
          phone: this.supporter.phoneNumber,
          agreesDisplayName: this.supporter.agreesDisplayName,
          signatureType: this.selection,
          cookieID: this.$cookie.get("_mkto_trk"),
          keepData: true,
          ssoId: this.$store.getters.ssoId,
          programName: this.programName,
        };

        if (browser) {
          payload.browsername = browser.name;
          payload.browserversion = browser.version;
          payload.browseros = browser.os;
        }

        console.log("Save petition payload", payload);

        await this.$store.dispatch("saveLead", {
          campaignId: this.datalayerName,
          lead: payload,
          saveSignature: this.supporter.agreesDisplayName,
        });

        console.log("Leaf Relief Petition submit success");

        // GTM
        gtm.formSubmitSuccessEvent(this.datalayerName);

        // Todo: Reset form
      } catch (err) {
        console.error("Leaf Relief Petition submit error");
        console.error(err);
      }
    },
    async saveDonation() {
      return new Promise((resolve, reject) => {
        this.$refs.Selector.commit();

        // Save pending donation
        // Populate the actual donation store and then save it
        this.donation.firstname = this.supporter.firstName;
        this.donation.lastname = this.supporter.lastName;
        this.donation.email = this.supporter.email;
        this.donation.phone = this.supporter.phoneNumber;
        this.donation.billing.country = null;
        this.donation.billing.state = null;
        this.donation.billing.postcode = this.supporter.postcode;

        this.$store
          .dispatch("saveDonation", {
            agreesDisplayName: this.supporter.agreesDisplayName,
          })
          .then((response) => {
            resolve();
          })
          .catch((error) => {
            console.error("saveDonation error", error);
            reject(error);
          });
      });
    },
  },
};
</script>

<style scoped>
.form-content {
  padding: var(--lrPadding);
}

.form-content button[type="button"] {
  justify-content: center;
  margin-bottom: 0.5rem;
}

.form-content .privacy-statement p {
  text-align: center;
  margin-bottom: 0 !important;
}

.submit-email-error {
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  color: var(--wwfWhite);
  background-color: var(--errorColor);
  border-radius: 4px;
}

.submit-email-error p {
  line-height: 20px;
}
</style>


// WEBPACK FOOTER //
// Details.vue?1bd18bce